import React from 'react';
import * as R from 'ramda';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { DrawerLayoutWrapper } from 'common';
import PropTypes from 'prop-types';
import { formatNumberWithCommas } from 'utils/helpers/format';
import { styled } from '@mui/material/styles';

const CheckIcon = styled(CheckCircleOutlineOutlinedIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: 120,
}));

const Subtitle = styled(Typography)({
  fontSize: 19,
  padding: 15,
});

const Title = styled(Typography)({
  fontSize: 25,
  fontWeight: 'bold',
  justifyContent: 'center',
  padding: 10,
  textAlign: 'center',
});

const AutoConfirmationStep = ({ reset, closeDrawer, reconsignmentState }) => {
  const formatOrigins = origins => {
    if (R.or(R.isNil(origins), R.isEmpty(origins))) return 'ANY';
    return origins.map(origin => {
      return `${origin.originName}`;
    });
  };

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={reset} sx={{ width: 150 }} variant="outlined">
        Create New
      </Button>
      <Button onClick={closeDrawer} sx={{ width: 150 }}>
        Done
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={actionButtons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={closeDrawer}
      title="New Auto Reconsignment"
    >
      <Box alignItems="center" display="flex" flexDirection="column" mb={3} mt={2}>
        <CheckIcon />
        <Title variant="Caption">AUTO RECONSIGNMENT {'\n'} SUBMITTED</Title>
      </Box>
      <Divider gap={32} />
      <Subtitle>Order Summary</Subtitle>
      <Stack>
        <TitleBox label="1. Details" />
        <ItemBox
          label="Affiliate"
          value={R.path(['inventoryOwner', 'customerNameShort'], reconsignmentState)}
        />
        <ItemBox label="Product" value={R.path(['product', 'productName'], reconsignmentState)} />
        <ItemBox
          label="From Location"
          value={R.path(['fromLocation', 'locationName'], reconsignmentState)}
        />
        <ItemBox
          label="To Location"
          value={R.path(['toLocation', 'locationName'], reconsignmentState)}
        />
        <ItemBox
          label="Threshold Volume"
          value={`${formatNumberWithCommas(R.prop('thresholdVolume', reconsignmentState))} bbl`}
        />
        <ItemBox
          label="Transfer Volume"
          value={`${formatNumberWithCommas(R.prop('transferVolume', reconsignmentState))} bbl`}
        />
        {!R.either(R.isEmpty, R.isNil)(R.prop('origins', reconsignmentState)) && (
          <Box mt={2}>
            <TitleBox label="2. Origin(s)" />
          </Box>
        )}
        <ItemBox label="Origins" value={formatOrigins(R.prop('origins', reconsignmentState))} />
      </Stack>
    </DrawerLayoutWrapper>
  );
};

const TitleBox = ({ label }) => {
  return (
    <Box bgcolor="graphic.light" mb={1} px={3} py={1} typography="subtitle1">
      {label}
    </Box>
  );
};
const ItemBox = ({ label, value }) => {
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between" px={2} py={1}>
      <Typography color="text.primary" noWrap sx={{ minWidth: 140 }} variant="body1">
        {label}
      </Typography>
      {Array.isArray(value) ? (
        <Stack direction="column" sx={{ maxWidth: 220 }}>
          {value.map(item => (
            <Typography
              color="text.primary"
              key={item}
              noWrap
              overflow="hidden"
              p={0.5}
              textAlign="left"
              textOverflow="ellipsis"
              variant="body1"
            >
              {item}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography color="text.primary" textAlign="right" variant="body1">
          {value}
        </Typography>
      )}
    </Stack>
  );
};

AutoConfirmationStep.propTypes = {
  closeDrawer: PropTypes.func.isRequired,
  reconsignmentState: PropTypes.shape({}).isRequired,
  reset: PropTypes.func.isRequired,
};

TitleBox.propTypes = {
  label: PropTypes.string.isRequired,
};

ItemBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]).isRequired,
};

export default AutoConfirmationStep;
